import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Eye, Pencil, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Area, Card, Confirm, Field, Input, OBtn, PageTitle, Pill, Skel, Toggle, YBtn, useSaver } from "@/admin/ui";
import { RichEditor } from "@/admin/RichEditor";
import { adminApi, fmtDate, useAdmin, useRefresh } from "@/admin/lib";

const VARS = ["company_name", "legal_name", "email", "phone", "whatsapp", "address", "gstin", "website_url", "support_hours", "grievance_officer", "jurisdiction_city", "retention_days", "last_updated"];

export const VarPanel = ({ onInsert, values }) => (
  <Card title="Variables" testId="variables-panel">
    <p className="mb-3 text-xs text-muted2">Click to insert at the cursor. Values come from General Settings and update everywhere automatically.</p>
    <div className="space-y-1.5">
      {VARS.map((v) => (
        <button key={v} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => onInsert(`{${v}}`)} className="flex w-full items-center justify-between gap-2 rounded-lg border border-line px-2.5 py-1.5 text-left text-xs hover:border-ocean" data-testid={`var-insert-${v}`}>
          <code className="font-bold text-ocean">{`{${v}}`}</code><span className="truncate text-muted2">{values?.[v]}</span>
        </button>
      ))}
    </div>
  </Card>
);

export const PreviewDialog = ({ html, onClose }) => (
  <Dialog open={html !== null} onOpenChange={onClose}>
    <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto rounded-2xl bg-white" data-testid="preview-dialog">
      <DialogHeader><DialogTitle className="font-heading text-navy">Preview with real values</DialogTitle></DialogHeader>
      <div className="legal-content" data-testid="preview-content" dangerouslySetInnerHTML={{ __html: html || "" }} />
    </DialogContent>
  </Dialog>
);

export function LegalList() {
  const { data, isLoading } = useAdmin("/admin/cms/legal-pages");
  return (
    <>
      <PageTitle title="Legal Pages" subtitle="Edit policies with a visual editor. Variables like {company_name} are replaced with your business details." />
      <Card>
        {isLoading ? <Skel /> : (
          <div className="divide-y divide-line" data-testid="legal-list">
            {(data || []).map((p) => (
              <div key={p.id} className="flex flex-wrap items-center gap-3 py-3.5" data-testid={`legal-row-${p.slug}`}>
                <div className="flex-1"><p className="font-semibold text-navy">{p.title}</p><p className="text-xs text-muted2">/{p.slug} · Last updated {fmtDate(p.last_updated, false)}</p></div>
                <Pill value={p.published ? "published" : "draft"} />
                <a href={`/${p.slug}`} target="_blank" rel="noopener noreferrer" className="grid h-8 w-8 place-items-center rounded-lg text-muted2 hover:text-ocean" aria-label="View"><Eye className="h-4 w-4" /></a>
                <Link to={`/admin/legal/${p.id}`} className="grid h-8 w-8 place-items-center rounded-lg text-muted2 hover:text-ocean" aria-label="Edit" data-testid={`legal-edit-${p.slug}`}><Pencil className="h-4 w-4" /></Link>
              </div>
            ))}
          </div>
        )}
        <p className="mt-4 text-xs text-muted2">Pricing page text is edited under <Link to="/admin/contact-page" className="font-bold text-ocean">Content → Contact & Pricing Text</Link>.</p>
      </Card>
    </>
  );
}

export function LegalEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const refresh = useRefresh();
  const { data } = useAdmin(`/admin/cms/legal-pages/${id}`);
  const { data: vars } = useAdmin("/admin/variables");
  const [doc, setDoc] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, run] = useSaver();
  const editor = useRef(null);
  const d = doc || data;
  if (!d) return <Skel rows={8} />;
  const set = (k, v) => setDoc({ ...d, [k]: v });

  const save = async () => {
    const r = await run(() => adminApi.put(`/admin/cms/legal-pages/${id}`, { title: d.title, content: d.content, seo_title: d.seo_title, seo_description: d.seo_description, published: d.published }));
    if (r) { setDoc(r.data); refresh(); }
  };
  const reset = async () => {
    try { const r = await adminApi.post(`/admin/legal-reset/${id}`); setDoc(r.data); toast.success("Default content restored"); refresh(); } catch { toast.error("Reset failed"); }
  };
  const doPreview = async () => setPreview((await adminApi.post("/admin/preview-vars", { content: d.content })).data.html);

  return (
    <>
      <PageTitle title={d.title} subtitle={`/${d.slug} · Last updated ${fmtDate(d.last_updated)}`}>
        <OBtn onClick={() => nav("/admin/legal")} testId="legal-back-btn"><ArrowLeft className="h-4 w-4" />Back</OBtn>
        <Confirm title="Reset to default content?" description="Your edits to this page will be replaced by the original template." actionLabel="Reset" onConfirm={reset}><OBtn testId="legal-reset-btn"><RotateCcw className="h-4 w-4" />Reset to default</OBtn></Confirm>
        <OBtn onClick={doPreview} testId="legal-preview-btn"><Eye className="h-4 w-4" />Preview with real values</OBtn>
        <YBtn loading={saving} onClick={save} testId="legal-save-btn">Save</YBtn>
      </PageTitle>
      <div className="grid gap-6 xl:grid-cols-[1fr_300px]">
        <div className="space-y-5">
          <Card>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Title"><Input value={d.title} onChange={(e) => set("title", e.target.value)} testId="legal-title-input" /></Field>
              <Toggle label="Published" checked={d.published} onChange={(v) => set("published", v)} testId="legal-published-toggle" />
              <Field label="SEO title"><Input value={d.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} testId="legal-seo-title-input" /></Field>
              <Field label="Meta description"><Area rows={2} value={d.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} testId="legal-seo-desc-input" /></Field>
            </div>
          </Card>
          <RichEditor ref={editor} value={d.content} onChange={(v) => set("content", v)} testId="legal-editor" />
        </div>
        <div className="xl:sticky xl:top-6 xl:self-start"><VarPanel values={vars} onInsert={(t) => editor.current?.insertText(t)} /></div>
      </div>
      <PreviewDialog html={preview} onClose={() => setPreview(null)} />
    </>
  );
}


